import { MarkerType } from '@web/components/Map/Map.constants'
import { markerZIndex } from '@web/components/Map/New/constant'
import { createMarkerIcon } from '@web/components/Map/New/marker.unit'
import { CustomMarkerOption } from '@web/components/Map/New/types'
import { isMarkerOverlapped } from '@web/components/Map/New/unit'
import { TimelineSection_TimelineFragment } from '@web/federation/gql/graphql'
import { getImageUrlFromMediaItem } from '@web/libs/media'
import { DOT_MARKER_OVERLAPPING_DISTANCE, MAX_IMAGE_MARKER_COUNT } from '@web/pages/timeline/constant'

export const convertTimelinesToMarkerOptions = (
  timelines: TimelineSection_TimelineFragment[],
): CustomMarkerOption[] => {
  const markerOptions = timelines
    .map((timeline): CustomMarkerOption | null => {
      if (!timeline.placeSummary?.coordinate) return null
      const { latitude, longitude } = timeline.placeSummary.coordinate

      const imageUrl = getImageUrlFromMediaItem(timeline.reviewGroup?.mainMediaItem)

      const icon = createMarkerIcon({
        type: imageUrl ? MarkerType.THUMBNAIL_SMALL : MarkerType.FOOTPRINT,
        thumbnailUrl: imageUrl,
      })

      return {
        id: timeline.id,
        imageUrl,
        position: { lat: latitude, lng: longitude },
        icon,
      } as CustomMarkerOption
    })
    .filter((marker): marker is CustomMarkerOption => !!marker)

  return markerOptions
}

/**
 * 마커 우선순위 노출 정책을 적용하여 필터링된 배열을 리턴합니다.
 * @param map 그려질 맵
 * @param markerOptions 대상 마커 배열
 * @param maxImageMarkerCount 노출 이미지 마커 수
 * @param maxImageMarkerCount 점 마커의 최대 거리 (이 거리 이하인 마커는 우선순위만 노출)
 * @returns 정책 적용한 마커 배열
 */
export function filterMarkersByPriority({
  map,
  markers,
  maxImageMarkerCount = MAX_IMAGE_MARKER_COUNT,
  limitDistance = DOT_MARKER_OVERLAPPING_DISTANCE,
  normalMarkerType,
}: {
  markers: CustomMarkerOption[]
  map: naver.maps.Map
  maxImageMarkerCount?: number
  limitDistance?: number
  normalMarkerType: MarkerType
}): CustomMarkerOption[] {
  const projection = map.getProjection() // 지도 투영체

  const queue: CustomMarkerOption[] = markers.slice() // 표시를 기다리는 마커 큐

  const displayMarkers: CustomMarkerOption[] = [] // 표시될 마커 배열

  let imageMarkerCount = 0 // 추가된 이미지 마커 숫자

  const maxIteration = 300 // 무한 루프 방어 코드 (하단 논리상 무한루프가 될 일은 없긴 함)
  let iteration = 0

  while (queue.length > 0 && iteration < maxIteration) {
    iteration++

    const marker = queue.shift()
    if (!marker) break

    const isOverlapping = displayMarkers.some(other =>
      isMarkerOverlapped({ p1: marker, p2: other, limitDistance, projection }),
    )

    // 이미지 마커인 경우
    if (marker.icon.type === MarkerType.THUMBNAIL || marker.icon.type === MarkerType.THUMBNAIL_SMALL) {
      if (!isOverlapping && imageMarkerCount < maxImageMarkerCount) {
        // 해당 이미지 마커보다 상단에 위치하는 마커 개수를 체크해서 상단 이미지 마커가 하단의 이미지 마커를 안가리도록 z-index를 보정합니다.
        const zIndexMargin = queue.filter(q => q.position.lat > marker.position.lat).length
        const imageMarker: CustomMarkerOption = { ...marker, zIndex: markerZIndex.IMAGE + zIndexMargin }
        displayMarkers.push(imageMarker)
        imageMarkerCount++
        continue
      }

      // 그게 아니라면 점마커로 변환 해서 맨 앞줄에 넣기
      queue.unshift({
        ...marker,
        icon: createMarkerIcon({
          type: normalMarkerType,
          //  name: marker.title,
        }),
      })
      continue
    }

    if (isOverlapping) continue
    displayMarkers.push(marker)
  }

  return displayMarkers
}
